import {
  DocumentStatus,
  IncidentReason,
  IncidentResolution,
  OrderStatus,
  PayerType,
  PaymentStatus,
  ProductType,
} from './types'

/** Цвет тега Element Plus: type задаёт цвет, effect — насыщенность. */
export type TagType = 'success' | 'warning' | 'danger' | 'info' | 'primary' | ''

export interface TagStyle {
  type: TagType
  effect: 'dark' | 'light'
}

/** Статусы заказа */
export const orderStatusLabels: Record<OrderStatus, string> = {
  new: 'Новый',
  pending: 'Ожидает оплаты',
  paid: 'Оплачен',
  completed: 'Выполнен',
  cancelled: 'Отменён',
  expired: 'Истёк',
  refunded: 'Возврат',
}

/**
 * Цвета статусов заказа:
 * paid      — зелёный: деньги получены.
 * completed — синий: услуги выполнены.
 * pending   — оранжевый: ожидается оплата.
 * cancelled / expired — серый: неактивное состояние.
 * refunded  — красный: произведён возврат.
 */
export const orderStatusStyles: Record<OrderStatus, TagStyle> = {
  new: { type: 'info', effect: 'light' },
  pending: { type: 'warning', effect: 'light' },
  paid: { type: 'success', effect: 'light' },
  completed: { type: 'primary', effect: 'dark' },
  cancelled: { type: 'info', effect: 'light' },
  expired: { type: 'info', effect: 'light' },
  refunded: { type: 'danger', effect: 'light' },
}

/** Статусы платежа */
export const paymentStatusLabels: Record<PaymentStatus, string> = {
  pending: 'Ожидает',
  success: 'Успешно',
  error: 'Ошибка',
  cancelled: 'Отменён',
  expired: 'Истёк',
  refunded: 'Возврат',
}

export const paymentStatusStyles: Record<PaymentStatus, TagStyle> = {
  pending: { type: 'warning', effect: 'light' },
  success: { type: 'success', effect: 'dark' },
  error: { type: 'danger', effect: 'light' },
  cancelled: { type: 'info', effect: 'light' },
  expired: { type: 'info', effect: 'light' },
  refunded: { type: 'danger', effect: 'light' },
}

/** Статусы документов */
export const documentStatusLabels: Record<DocumentStatus, string> = {
  issued: 'Выставлен',
  paid: 'Оплачен',
  cancelled: 'Аннулирован',
  refunded: 'Возврат',
}

export const documentStatusStyles: Record<DocumentStatus, TagStyle> = {
  issued: { type: 'warning', effect: 'light' },
  paid: { type: 'success', effect: 'light' },
  cancelled: { type: 'info', effect: 'light' },
  refunded: { type: 'danger', effect: 'light' },
}

/** Типы документов */
export const documentTypeLabels = {
  invoice: 'Счёт',
  act: 'Акт',
}

/** Типы плательщиков */
export const payerTypeLabels: Record<PayerType, string> = {
  individual: 'Физлицо',
  legal: 'Юрлицо / ИП',
}

/** Причины инцидентов */
export const incidentReasonLabels: Record<IncidentReason, string> = {
  amount_mismatch: 'Сумма не совпала',
  duplicate_payment: 'Повторная оплата',
  order_not_payable: 'Заказ нельзя оплатить',
  payment_status_conflict: 'Конфликт статусов платежа',
}

/** Решения по инцидентам */
export const incidentResolutionLabels: Record<IncidentResolution, string> = {
  accepted_manually: 'Платёж принят',
  refunded_manually: 'Возврат сделан вручную',
  dismissed: 'Вмешательство не требуется',
}

/** Типы продуктов */
export const productTypeLabels: Record<ProductType, string> = {
  tariff_pro: 'Тариф PRO',
  tariff_company: 'Тариф «Компания»',
  tariff_flow_start: 'Тариф Flow Start',
  tariff_flow_plus: 'Тариф Flow Plus',
  tariff_flow_max: 'Тариф Flow Max',
  tariff_pro_archive: 'Тариф PRO (архив)',
  ads_fixed: 'Закрепление объявления',
  ads_demand_contacts: 'Контакты закупок',
  company_ownership: 'Подтверждение владения компанией',
}

/** Опции для el-select из словаря. */
export const toOptions = (labels: Record<string, string>) =>
  Object.keys(labels).map(value => ({ value, label: labels[value] }))

/** Названия действий журнала аудита (payment_audit_log.action). Неизвестные коды показываются как есть. */
export const auditActionLabels: Record<string, string> = {
  // заказы
  'order.create': 'Заказ оформлен пользователем',
  'order.create_for_user': 'Заказ создан администратором за пользователя',
  'order.pay': 'Заказ оплачен',
  'order.complete': 'Заказ завершён (услуги выданы)',
  'order.cancel': 'Заказ отменён',
  'order.expire': 'Заказ закрыт по сроку',
  'order.refund': 'Возврат по заказу',
  'mail.invoice_issued': 'Письмо: счёт выставлен',
  'mail.order_completed': 'Письмо: услуги подключены',
  'mail.act_issued': 'Письмо: акт выпущен',
  'mail.invoice_reminder': 'Письмо: напоминание об оплате',
  'mail.invoice_expired': 'Письмо: срок счёта истёк',
  'mail.order_refunded': 'Письмо: возврат по заказу',
  'order.refresh_payments': 'Сверка платежей со шлюзом',
  'order.accept_late_payment': 'Принят запоздалый платёж',
  'order.mark_activated_manually': 'Услуги отмечены выданными вручную',
  'order.retry_activation': 'Повторная выдача услуг',
  'order.retry_activation_failed': 'Повторная выдача услуг не удалась',
  'order.activation_failed': 'Выдача услуг не удалась',
  // выдача услуг
  'service.activate': 'Услуга выдана',
  'service.activate_direct': 'Услуга выдана (оплата в Bitrix)',
  'service.deactivate': 'Услуга отозвана',
  'service.deactivate_incomplete': 'Услугу не удалось отозвать полностью',
  // платежи
  'payment.create': 'Платёж создан',
  'payment.succeed': 'Платёж успешен',
  'payment.error': 'Платёж завершился ошибкой',
  'payment.cancelled': 'Платёж отменён',
  'payment.expired': 'Платёж истёк',
  'payment.refunded': 'Платёж возвращён',
  // счета и акты
  'invoice.create': 'Счёт выставлен',
  'invoice.confirm_payment': 'Оплата счёта подтверждена',
  'document.cancel': 'Документ аннулирован',
  'document.regenerate_pdf': 'PDF перегенерирован',
  'act.issue': 'Акт выпущен автоматически',
  'act.issue_manually': 'Акт выпущен вручную',
  'act.issue_failed': 'Акт не выпущен',
  // инциденты
  'incident.open': 'Открыт инцидент',
  'incident.resolve': 'Инцидент закрыт',
  // доступы
  'access_period.grant': 'Выдан период доступа',
  'access_period.revoke': 'Период доступа отозван',
  'access_period.import': 'Перенос периодов доступа из Bitrix',
  'pass.grant': 'Выдан пропуск',
  'pass.revoke': 'Пропуск отозван',
  'pass.claim_with_quota': 'Пропуск открыт из квоты пользователем',
  'quota.change': 'Изменена квота',
  // компании
  'company_ownership.check_requested': 'Запущена проверка владения компанией',
  'buyer_requisites.submit_failed': 'Реквизиты плательщика не отправлены в профиль компании',
  // товары
  'product.create': 'Товар создан',
  'product.update': 'Товар изменён',
  'product.archive': 'Товар заархивирован',
}

/** Название действия журнала; неизвестный код показывается как есть. */
export const auditActionLabel = (action: string): string =>
  auditActionLabels[action] || action
